import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { auth, fmtAmt, lbl, inp, sel as selStyle, card, AreaRangeInput } from '../RealTradePage';

// ─────────────────────────────────────────────
// 단지 스크리닝 탭 - /api/real-trades/screen
//
//  복합조건으로 단지를 걸러내고 정렬한다.
//  · 1차 드롭다운 = 정렬 기준 (거래량/매매율/등락폭)
//  · 2차 드롭다운 = 평형대 (거래를 해당 평형으로 한정해 집계)
//  · 그 외 조건은 최소/최대 입력
//
// [등락폭 주의]
//  앞 3개월 대비 뒤 3개월 평당가 변화율이다.
//  평형을 지정하지 않으면 그 달에 거래된 평형 구성에 따라 값이 크게 흔들린다.
//  → 화면에 거래건수·개월수를 함께 표시해 판단을 돕는다.
// ─────────────────────────────────────────────

// 1차: 정렬 기준
const SORT_GROUPS = [
  { key: 'cnt',      label: '전체 (거래량순)',  asc: 'cnt_desc',      desc: 'cnt_desc' },
  { key: 'turnover', label: '세대수대비 매매율', asc: 'turnover_asc',  desc: 'turnover_desc' },
  { key: 'change',   label: '등락폭',           asc: 'change_asc',    desc: 'change_desc' },
  { key: 'amt',      label: '평균 거래가',       asc: 'amt_asc',       desc: 'amt_desc' },
  { key: 'unit',     label: '평균 평당가',       asc: 'unit_asc',      desc: 'unit_desc' },
  { key: 'hhld',     label: '전체 세대수',       asc: 'hhld_asc',      desc: 'hhld_desc' },
  { key: 'vlrat',    label: '용적률',           asc: 'vlrat_asc',     desc: 'vlrat_desc' },
  { key: 'agent',     label: '반경 내 중개사무소 수', asc: 'agent_asc',  desc: 'agent_desc' },
  { key: 'per_agent', label: '중개사 1곳당 세대수',   asc: 'per_agent_desc', desc: 'per_agent_desc' },
];

// 2차: 평형대
const BUCKETS = [
  { v: '',  t: '전체 평형' },
  { v: '1', t: '~40㎡ (12평↓)' },
  { v: '2', t: '40~60㎡ (12~18평)' },
  { v: '3', t: '60~85㎡ (18~26평)' },
  { v: '4', t: '85~102㎡ (26~31평)' },
  { v: '5', t: '102~135㎡ (31~41평)' },
  { v: '6', t: '135㎡~ (41평↑)' },
];

export default function AptScreen({ lawdCd, from, to, regions, propType, onPickApt, zone}) {
  // 정렬
  const [sortKey, setSortKey] = useState('turnover');
  const [descend, setDescend] = useState(true);   // true=내림차순(높은순)
  const [bucket, setBucket]   = useState('');

  // 검색 범위
  const [scope, setScope] = useState('one');      // one=선택지역 / all=전체

  // 조건 (빈 문자열이면 미적용)
  const [c, setC] = useState({
    hhld_min: '100', hhld_max: '',
    cnt_min: '5', months_min: '',
    turnover_min: '', turnover_max: '',
    amt_min: '', amt_max: '',
    unit_min: '', unit_max: '',
    change_min: '', change_max: '',
    agent_min: '', agent_max: '', per_agent_min: '',
    year_min: '', year_max: '',
    vlrat_min: '', vlrat_max: '', bcrat_min: '', bcrat_max: '',
    ar_min: '', ar_max: '',
  });
  const set = (k, v) => setC(p => ({ ...p, [k]: v }));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // 지역이 바뀌면 이전 지역의 검색 결과를 비운다.
  // (검색 조건 자체는 유지해 같은 기준으로 지역만 바꿔 비교할 수 있게 한다)
  useEffect(() => {
    setRows([]);
    setSearched(false);
  }, [lawdCd]);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const g = SORT_GROUPS.find(x => x.key === sortKey);
      const q = new URLSearchParams({
        from, to, size: 200, prop_type: propType,
        sort: descend ? g.desc : g.asc,
      });
      if (scope === 'one') {
        if (lawdCd) q.set('lawd_cd', lawdCd);
        else if (zone) q.set('zone', zone);
      }
      // 값이 입력된 조건만 전달
      Object.entries(c).forEach(([k, v]) => {
        if (v !== '' && v != null) q.set(k, v);
      });

      const res = await axios.get(`/api/real-trades/screen?${q}`, auth());
      setRows(res.data.rows || []);
      setSearched(true);
    } catch (e) {
      alert('검색 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [sortKey, descend, scope, lawdCd, from, to, c]);

  const sggName = regions.find(r => r.lawd_cd === lawdCd)?.sgg_nm || '';

  return (
    <div>
      {/* ── 정렬 + 평형 (1차/2차 드롭다운) ── */}
      <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={lbl}>정렬</label>
        <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={selStyle}>
          {SORT_GROUPS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
        </select>

        <button onClick={() => setDescend(d => !d)} style={btnToggle}>
          {descend ? '▼ 높은순' : '▲ 낮은순'}
        </button>

        <label style={{ ...lbl, marginLeft: 8 }}>범위</label>
        <select value={scope} onChange={e => setScope(e.target.value)} style={selStyle}>
          <option value="one">선택 지역 ({sggName})</option>
          <option value="all">전체 62개 지역</option>
        </select>
      </div>

      {/* ── 상세 조건 ── */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#475569' }}>
          검색 조건 <span style={{ fontWeight: 400, color: '#94a3b8' }}>(비워두면 미적용)</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <AreaRangeInput
            arMin={c.ar_min} arMax={c.ar_max}
            onMin={v => set('ar_min', v)} onMax={v => set('ar_max', v)} />

          {/* 면적 조건은 집계 테이블을 쓸 수 없어 원본 394만 건을 훑는다.
              지역을 지정하면 인덱스가 걸려 즉시 나오지만,
              전체 지역이면 10초 이상 걸리므로 미리 알려준다. */}
          {(c.ar_min || c.ar_max) && scope === 'all' && (
            <div style={{ marginLeft: 106, fontSize: 11, color: '#b45309',
                          background: '#fffbeb', padding: '4px 8px',
                          borderRadius: 4, display: 'inline-block' }}>
              ⚠ 전체 지역 + 면적 조건은 조회에 <b>10초 이상</b> 걸릴 수 있습니다.
              위에서 지역을 지정하면 바로 나옵니다.
            </div>
          )}

          <Range label="전체 세대수" unit="세대"
                 min={c.hhld_min} max={c.hhld_max}
                 onMin={v => set('hhld_min', v)} onMax={v => set('hhld_max', v)} />

          <Range label="매매율" unit="%"
                 min={c.turnover_min} max={c.turnover_max}
                 onMin={v => set('turnover_min', v)} onMax={v => set('turnover_max', v)}
                 hint="거래건수 ÷ 세대수" />

          <Range label="등락폭" unit="%"
                 min={c.change_min} max={c.change_max}
                 onMin={v => set('change_min', v)} onMax={v => set('change_max', v)}
                 hint="하락은 음수로 (예: -5)" />

          <Range label="평균 거래가" unit="만원"
                 min={c.amt_min} max={c.amt_max}
                 onMin={v => set('amt_min', v)} onMax={v => set('amt_max', v)}
                 hint="10억 = 100000" />

          <Range label="평균 평당가" unit="만원"
                 min={c.unit_min} max={c.unit_max}
                 onMin={v => set('unit_min', v)} onMax={v => set('unit_max', v)} />

          <Range label="건축년도" unit="년"
                 min={c.year_min} max={c.year_max}
                 onMin={v => set('year_min', v)} onMax={v => set('year_max', v)}
                 hint="예: 2010 ~ 2020" />

          <Range label="용적률" unit="%"
                 min={c.vlrat_min} max={c.vlrat_max}
                 onMin={v => set('vlrat_min', v)} onMax={v => set('vlrat_max', v)}
                 hint="낮을수록 재건축 여지" />

          <Range label="건폐율" unit="%"
                 min={c.bcrat_min} max={c.bcrat_max}
                 onMin={v => set('bcrat_min', v)} onMax={v => set('bcrat_max', v)} />
          <Range label="중개사무소 수" unit="곳"
                 min={c.agent_min} max={c.agent_max}
                 onMin={v => set('agent_min', v)} onMax={v => set('agent_max', v)}
                 hint="관리탭에서 설정한 반경 기준" />

          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ ...lbl, width: 100 }}>1곳당 세대수</span>
            <input value={c.per_agent_min} onChange={e => set('per_agent_min', e.target.value)}
                   placeholder="이상" style={{ ...inp, width: 80 }} />
            <span style={{ fontSize: 12, color: '#64748b' }}>세대</span>
            <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>
              높을수록 중개사 대비 세대가 많음 (경쟁 적음)
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ ...lbl, width: 100 }}>최소 거래건수</span>
            <input value={c.cnt_min} onChange={e => set('cnt_min', e.target.value)}
                   style={{ ...inp, width: 70 }} />
            <span style={{ ...lbl, marginLeft: 12 }}>최소 개월수</span>
            <input value={c.months_min} onChange={e => set('months_min', e.target.value)}
                   style={{ ...inp, width: 70 }} />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              표본이 적으면 등락폭이 부정확합니다
            </span>
          </div>
        </div>

        <button onClick={search} style={{ ...btnP, marginTop: 12 }}>
          🔍 검색
        </button>
      </div>

      {/* ── 결과 ── */}
      {loading && (
        <div style={{ padding: 20, color: '#3b82f6' }}>
          검색 중…
          {(c.ar_min || c.ar_max) && scope === 'all' && (
            <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>
              (전체 지역 면적 검색은 시간이 걸립니다)
            </span>
          )}
        </div>
      )}

      {searched && !loading && (
        <>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#475569' }}>
            검색 결과 <b>{rows.length}</b>개
            <span style={{ color:'#94a3b8', marginLeft:8, fontSize:12 }}>
              (행을 클릭하면 단지분석으로 이동합니다)
            </span>
            {rows.length >= 200 && <span style={{ color: '#f59e0b' }}> (상위 200개만 표시)</span>}
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  {['지역','법정동','단지명','세대수','준공','용적률','건폐율','거래',
                    '매매율','평균가','평당가','등락폭','중개사','1곳당'].map(h =>
                    <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}
                      onClick={() => onPickApt && onPickApt({
                        apt_nm: r.apt_nm, umd_nm: r.umd_nm, jibun: r.jibun,
                      })}
                      style={{ borderTop: '1px solid #f1f5f9',
                               cursor: onPickApt ? 'pointer' : 'default' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={td}>{r.sgg_nm}</td>
                    <td style={td}>{r.umd_nm}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{r.apt_nm}</td>
                    <td style={tdR}>{r.hhld_cnt?.toLocaleString() || '-'}</td>
                    <td style={tdR}>{r.build_year || '-'}</td>
                    <td style={tdR}>{r.vl_rat != null ? `${r.vl_rat}%` : '-'}</td>
                    <td style={tdR}>{r.bc_rat != null ? `${r.bc_rat}%` : '-'}</td>
                    <td style={tdR}>{r.period_cnt}</td>
                    <td style={{ ...tdR, fontWeight: 700,
                      color: r.turnover_pct >= 8 ? '#dc2626' : '#1e293b' }}>
                      {r.turnover_pct != null ? `${r.turnover_pct}%` : '-'}
                    </td>
                    <td style={tdR}>{fmtAmt(r.avg_amt)}</td>
                    <td style={tdR}>{r.avg_unit?.toLocaleString()}</td>
                    <td style={{ ...tdR, fontWeight: 700,
                      color: r.change_pct > 0 ? '#dc2626'
                           : r.change_pct < 0 ? '#2563eb' : '#64748b' }}>
                      {r.change_pct == null ? '-'
                        : `${r.change_pct > 0 ? '▲' : r.change_pct < 0 ? '▼' : '─'}${Math.abs(r.change_pct)}%`}
                      {!r.reliable && <span title="표본 부족" style={{ marginLeft: 3 }}>⚠</span>}
                    </td>
                    <td style={tdR}>{r.agent_cnt ?? '-'}</td>
                    <td style={{ ...tdR, fontWeight: 600,
                      color: r.hhld_per_agent >= 200 ? '#059669' : '#1e293b' }}>
                      {r.hhld_per_agent ?? '-'}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={11} style={{ ...td, textAlign: 'center',
                    padding: 30, color: '#94a3b8' }}>
                    조건에 맞는 단지가 없습니다
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: 10, fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
            ※ <b>등락폭</b>은 앞 3개월 대비 뒤 3개월의 평당가 변화율입니다.
            평형대를 <b>전체</b>로 두면, 그 달에 거래된 평형 구성이 바뀌는 것만으로도
            큰 값이 나올 수 있습니다. 정확한 추세를 보려면 평형대를 지정하세요.<br/>
            ※ ⚠ 표시는 표본 부족(6개월 미만 또는 20건 미만)이라는 뜻입니다.<br/>
            ※ <b>용적률·건폐율</b>은 건축물대장 기준입니다. 오래된 단지는 대장에 값이 없어
            <b>-</b> 로 표시되며, 용적률 조건으로 검색하면 이런 단지는 결과에서 빠집니다.
          </p>
        </>
      )}
    </div>
  );
}

// 최소~최대 입력 한 줄
function Range({ label, unit, min, max, onMin, onMax, hint }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ ...lbl, width: 100 }}>{label}</span>
      <input value={min} onChange={e => onMin(e.target.value)}
             placeholder="이상" style={{ ...inp, width: 80 }} />
      <span style={{ color: '#94a3b8' }}>~</span>
      <input value={max} onChange={e => onMax(e.target.value)}
             placeholder="이하" style={{ ...inp, width: 80 }} />
      <span style={{ fontSize: 12, color: '#64748b' }}>{unit}</span>
      {hint && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>{hint}</span>}
    </div>
  );
}

const th  = { padding: '9px 8px', textAlign: 'left', fontWeight: 600,
              color: '#475569', whiteSpace: 'nowrap' };
const td  = { padding: '8px', whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };
const btnP = { padding: '8px 20px', background: '#3b82f6', color: '#fff',
               border: 'none', borderRadius: 4, cursor: 'pointer',
               fontSize: 14, fontWeight: 600 };
const btnToggle = { padding: '6px 12px', background: '#fff', border: '1px solid #cbd5e1',
                    borderRadius: 4, cursor: 'pointer', fontSize: 13, minWidth: 90 };